<template>
	<div style="position: relative">
		<div style="display: flex; flex-direction: column; gap: 10px">
			<div style="display: flex; align-items: center; gap: 12px">
				<div>
					<button
						class="panel-close-button"
						@click="$emit('closePanel')"
						:title="t('panels.editorPanel.minimizeTooltip')"
					>
						<svg-icon type="mdi" :path="mdiChevronLeft"></svg-icon>
					</button>
				</div>
				<div>
					<h4>{{ $t("common.panels.editor") }} {{ store.article.isReadOnly ? t("panels.editorPanel.readOnlySuffix") : "" }}</h4>
				</div>
			</div>

			<div style="display: flex; flex-direction: row; gap: 6px">
				<div v-if="!store.article.isAssembly && store.article.rawMaterialId > 0">
					<button
						@click="store.setEditorMode(EditorMode.FeatureMode)"
						:class="store.editorMode == EditorMode.FeatureMode ? 'active-state' : ''"
						class="svg-button"
						:title="t('panels.editorPanel.featureModeTooltip')"
					>
						<img :src="Edit" />
					</button>
				</div>
				<div>
					<button
						@click="store.setEditorMode(EditorMode.MeasuringMode)"
						:class="store.editorMode == EditorMode.MeasuringMode ? 'active-state' : ''"
						class="svg-button"
						:title="t('panels.editorPanel.measuringModeTooltip')"
					>
						<img :src="Ruler" />
					</button>
				</div>
				<div>
					<button
						@click="store.setEditorMode(EditorMode.WeldMode)"
						:disabled="!store.article.isWeldingAllowed"
						:class="store.editorMode == EditorMode.WeldMode ? 'active-state' : ''"
						class="svg-button"
						:title="
							store.article.isWeldingAllowed
								? t('panels.editorPanel.weldModeTooltip')
								: t('panels.editorPanel.weldModeDisabledTooltip')
						"
					>
						<img :src="Welding" />
					</button>
				</div>
				<div
					v-if="
						!store.article.isAssembly &&
						store.article.rawMaterialId > 0 &&
						store.isRollDirectionRelevant
					"
				>
					<button
						@click="store.setEditorMode(EditorMode.RollDirectionMode)"
						:class="
							store.editorMode == EditorMode.RollDirectionMode ? 'active-state' : ''
						"
						class="svg-button"
						:title="t('panels.editorPanel.rollDirectionModeTooltip')"
					>
						<img :src="Arrows" />
					</button>
				</div>
				<div v-if="!store.article.isAssembly && store.article.rawMaterialId > 0">
					<button
						:disabled="store.article.isReadOnly"
						class="svg-button"
						@click="changeUpperside()"
						:title="t('panels.editorPanel.switchTopSideTooltip')"
					>
						<img :src="Eye" />
					</button>
				</div>
			</div>
			<div style="display: flex; flex-direction: row; gap: 6px">
				<div>
					<button @click="$emit('fitCameraToObject')">
						<img :src="Fullscreen" />
						{{ $t("panels.editorPanel.zoomToFit") }}
					</button>
				</div>
			</div>
			<div
				v-if="store.article.hasElevatedRights"
				style="display: flex; flex-direction: row; gap: 6px; align-items: center"
			>
				<div>
					<button @click="rotate" class="svg-button">
						<img :src="Rotate" />
					</button>
				</div>
				<div>
					<label style="display: flex; align-items: center; gap: 6px">
						<input type="checkbox" v-model="skipValidation" />
						{{ $t("panels.editorPanel.noValidationLabel") }}
					</label>
				</div>
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
import Ruler from "@/assets/Icons/square_foot-24px.svg";
import Eye from "@/assets/Icons/visibility-24px.svg";
import Edit from "@/assets/Icons/create-24px.svg";
import Arrows from "@/assets/Icons/open_in_full-24px.svg";
import Fullscreen from "@/assets/Icons/fullscreen-24px.svg";
import Welding from "@/assets/Icons/welding.svg";
import Rotate from "@/assets/Icons/rotate.svg";
import SvgIcon from "@jamescoyle/vue-icon";
import { mdiChevronLeft } from "@mdi/js";
import { EditorMode, TaskType } from "@/model/Enums";
import { useMainStore } from "@/store/mainStore";
import { storeToRefs } from "pinia";
import { LoadingState } from "@/model/Enums";
import { useI18n } from "vue-i18n";

const emit = defineEmits(["fitCameraToObject", "closePanel"]);

const { t } = useI18n();
const store = useMainStore();
const { skipValidation } = storeToRefs(store);

const changeUpperside = () => {
	store.cam.modifyFeatures(LoadingState.ChangingAnchorFace, [
		{
			type: TaskType.SwitchAnchorFace,
			radius: 0,
			innerRadius: 0,
			exportIds: [],
			isOnAnchorFace: true,
		},
	]);
};

const rotate = () => {
	// Spin object quicker (states 1-3) or stop it spinning (state 0).
	if (store.rotateObjectState === 3) {
		store.rotateObjectState = 0;
	} else {
		store.rotateObjectState++;
	}
};
</script>

<style scoped>
.active-state {
	background-color: var(--blexon-color);
}
</style>
